import { StatusPerishable } from 'app/components/status-perishable';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import { localeAwareFormat } from 'app/lib/date';
import { useAllAddresses } from 'app/network/address';
import { useOneListing, useOneListingDistance } from 'app/network/listing';
import { LoanFilterKey, useAllLoans } from 'app/network/loan';
import { createTheme } from 'app/shopify';
import type { TextMessage } from 'app/types/chat';
import { ListingSubtype } from 'app/types/listing';
import { useTranslation } from 'react-i18next';
import { formatTransportLocation, formatTransportPublicLocation } from './transport-listing-message.utils';

const theme = createTheme();

interface TransportListingMessageProps {
  listingId: string;
  ogTag: NonNullable<TextMessage['og_tag']>;
  onPress: () => void;
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <Text fontSize={14} lineHeight={20}>
      {label} : <Text fontWeight="700">{value}</Text>
    </Text>
  );
}

export function TransportListingMessage({ listingId, ogTag, onPress }: TransportListingMessageProps) {
  const { t } = useTranslation('one-channel');
  const { data: listing } = useOneListing(listingId, 'transport-support-message');
  const { data: listingDistance } = useOneListingDistance(listingId);
  const { data: acceptedLoans } = useAllLoans(LoanFilterKey.Accepted);
  const { data: addresses } = useAllAddresses();

  const notAvailable = t('transportListing.notAvailable');
  const imageUri = listing?.picture ?? ogTag['og:image']?.secure_url ?? ogTag['og:image']?.url;
  const pickupLoan = acceptedLoans?.find((loan) => loan.listingId === listingId);
  const transportDate = pickupLoan?.start;
  const date = transportDate
    ? localeAwareFormat(new Date(transportDate), t('dateFormat_MMMMdYYYY', { ns: 'common' }))
    : notAvailable;
  const memberLocation = pickupLoan?.lender.profile ?? listing?.user.profile;
  const departure =
    (memberLocation?.postalCode ? formatTransportLocation(memberLocation) : undefined) ??
    formatTransportPublicLocation(listingDistance?.publicLocation?.label ?? listingDistance?.city) ??
    formatTransportLocation(memberLocation) ??
    notAvailable;
  const destination = formatTransportLocation(addresses?.[0]) ?? notAvailable;

  return (
    <Column
      backgroundColor="surface.default"
      borderColor="border.brand-alt"
      borderRadius="radius.xl"
      borderWidth={1}
      gap="spacing.general.x4"
      padding="spacing.general.x6"
      testID="transport-listing-card"
      width="100%"
    >
      <Row alignItems="center" gap="spacing.general.x2" width="100%">
        {imageUri ? (
          <PCImage
            contentFit="cover"
            source={{ uri: imageUri }}
            style={{
              backgroundColor: theme.colors['surface.tertiary-neutral'],
              borderRadius: 10,
              flexShrink: 0,
              height: 80,
              width: 80,
            }}
          />
        ) : null}

        <Column flex={1} gap="spacing.general.x1" style={{ minWidth: 0 }}>
          <Text fontSize={16} lineHeight={20} numberOfLines={1}>
            {listing?.item.name ?? ogTag['og:title']}
          </Text>
          <Text color="text.tertiary" fontSize={14} lineHeight={18} numberOfLines={2}>
            {listing?.description ?? ogTag['og:description']}
          </Text>
        </Column>

        <Button
          accessibilityLabel={t('transportListing.view')}
          onPress={onPress}
          style={{
            alignSelf: 'center',
            backgroundColor: theme.colors['surface.inverse.default'],
            borderRadius: theme.borderRadii['radius.full'],
            minHeight: 36,
            paddingHorizontal: theme.spacing['spacing.general.x3'],
          }}
          variant="small-pill"
        >
          <Text color="text.inverse.primary" fontSize={14} fontWeight="600">
            {t('transportListing.view')}
          </Text>
        </Button>
      </Row>

      {listing?.subtype === ListingSubtype.expirable ? <StatusPerishable /> : null}

      <Column gap="spacing.general.x1">
        <DetailRow label={t('transportListing.date')} value={date} />
        <DetailRow label={t('transportListing.departure')} value={departure} />
        <DetailRow label={t('transportListing.destination')} value={destination} />
      </Column>
    </Column>
  );
}

export { formatTransportLocation, formatTransportPublicLocation } from './transport-listing-message.utils';
